import React, { useCallback } from "react";
import {
    buildCounterpartyRefLabel,
    CounterpartyReference,
} from "@common/schemas/counterpartyReference.schema";
import { MembersIcon } from "@icons";
import { useOverlays } from "contexts/OverlayContext";
import { useCounterpartyRefContextMaybe } from "@components/tasks/CounterpartyRefContext";
import { RefChip, RefChipProps } from "./RefChip";
import { useRefChipHoverFocus } from "./useRefChipHoverFocus";

interface CounterpartyRefChipProps extends Omit<
    RefChipProps,
    "icon" | "label"
> {
    reference: CounterpartyReference;
}

export const CounterpartyRefChip: React.FC<CounterpartyRefChipProps> =
    React.memo(function CounterpartyRefChip({ reference, ...handlers }) {
        return (
            <RefChip
                icon={MembersIcon}
                label={reference.label ?? buildCounterpartyRefLabel(reference)}
                {...handlers}
            />
        );
    });

/**
 * Chip in a sent message: hover highlights the counterparty row in the task
 * table; click opens the counterparty editor drawer.
 */
export const MessageCounterpartyRefChip: React.FC<{
    reference: CounterpartyReference;
}> = React.memo(function MessageCounterpartyRefChip({ reference }) {
    const { openOverlay } = useOverlays();
    const counterpartyRefCtx = useCounterpartyRefContextMaybe();
    const { handleMouseEnter, handleMouseLeave } = useRefChipHoverFocus({
        id: reference.counterpartyId,
        setFocusedId: counterpartyRefCtx?.setFocusedCounterpartyId,
    });

    const handleClick = useCallback(() => {
        openOverlay("counterpartyEditorDrawer", {
            counterpartyId: reference.counterpartyId,
        });
    }, [openOverlay, reference.counterpartyId]);

    return (
        <CounterpartyRefChip
            reference={reference}
            onClick={handleClick}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        />
    );
});
